import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, UploadCloud, FileText, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useCreateContrato, type ContratoTipo, type CreateContratoInput } from '@/lib/hooks/contratos';
import { uploadFile } from '@/lib/hooks/pagamentos';
import { cn, fmtCpfCnpj } from '@/lib/utils';

interface FormValues {
  tipo: ContratoTipo | '';
  numero: string;
  contratadoCpfCnpj: string;
  contratadoNome: string;
  objeto: string;
  valorTotal: string;
  dataInicio: string;
  dataFim: string;
  r2Key: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TIPOS: { value: ContratoTipo; label: string }[] = [
  { value: 'PRESTACAO_SERVICOS', label: 'Prestação de serviços' },
  { value: 'FORNECIMENTO', label: 'Fornecimento' },
  { value: 'LOCACAO', label: 'Locação' },
  { value: 'OUTRO', label: 'Outro' },
];

const ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

const DEFAULTS: FormValues = {
  tipo: '',
  numero: '',
  contratadoCpfCnpj: '',
  contratadoNome: '',
  objeto: '',
  valorTotal: '',
  dataInicio: '',
  dataFim: '',
  r2Key: '',
};

export function ContratoDrawer({ workspaceId, open, onOpenChange }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const createMut = useCreateContrato(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) {
      reset(DEFAULTS);
      setUploadedName(null);
      setUploading(false);
    }
  }, [open, reset]);

  const r2Key = watch('r2Key');

  async function handleFile(file: File) {
    if (!ACCEPT.includes(file.type)) {
      toast.error('Formato não suportado. Use PDF, JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Arquivo excede 15 MB.');
      return;
    }
    setUploading(true);
    try {
      const key = await uploadFile(file, 'contratos');
      setValue('r2Key', key, { shouldDirty: true });
      setUploadedName(file.name);
      toast.success('Contrato enviado.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function removeFile() {
    setValue('r2Key', '', { shouldDirty: true });
    setUploadedName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function onSubmit(values: FormValues) {
    if (!values.tipo) {
      toast.error('Selecione o tipo de contrato.');
      return;
    }
    const payload: CreateContratoInput = {
      tipo: values.tipo as ContratoTipo,
      contratadoCpfCnpj: values.contratadoCpfCnpj.replace(/\D/g, ''),
      contratadoNome: values.contratadoNome,
      objeto: values.objeto,
      valorTotal: values.valorTotal.replace(',', '.'),
      dataInicio: values.dataInicio,
    };
    if (values.numero) payload.numero = values.numero;
    if (values.dataFim) payload.dataFim = values.dataFim;
    if (values.r2Key) payload.r2Key = values.r2Key;

    try {
      await createMut.mutateAsync(payload);
      toast.success('Contrato cadastrado.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cadastrar contrato.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Novo lançamento"
            title="Cadastrar contrato"
            description="Vinculado ao convênio para fins de prestação de contas."
          />

          <SheetBody className="space-y-5">
            {/* Dropzone PDF */}
            <div>
              <Label className="mb-2 block">Arquivo do contrato</Label>
              {r2Key ? (
                <div className="flex items-center gap-3 rounded-2 border border-card-stroke bg-card-soft p-3">
                  <div className="grid h-10 w-10 place-items-center rounded-1 bg-mint-soft text-mint-deep">
                    <FileText className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-fg truncate">{uploadedName ?? 'arquivo enviado'}</p>
                    <p className="text-[11px] text-fg-muted font-mono truncate">{r2Key}</p>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="grid h-8 w-8 place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card"
                    aria-label="Remover"
                  >
                    <X className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'flex flex-col items-center justify-center gap-2 rounded-2 border-2 border-dashed px-4 py-7 cursor-pointer transition-colors',
                    dragOver
                      ? 'border-mint-deep bg-mint-soft/40'
                      : 'border-card-stroke bg-card-soft hover:border-mint-deep/60',
                  )}
                >
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-mint-soft text-mint-deep">
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                    ) : (
                      <UploadCloud className="h-4 w-4" strokeWidth={1.8} />
                    )}
                  </div>
                  <p className="text-sm font-medium text-fg">
                    {uploading ? 'Enviando…' : 'Arraste ou clique para enviar'}
                  </p>
                  <p className="text-[11px] text-fg-muted">PDF, JPG, PNG ou WebP · até 15 MB</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT.join(',')}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </div>

            {/* Tipo + Número */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Tipo</Label>
                <Controller
                  control={control}
                  name="tipo"
                  rules={{ required: 'Obrigatório' }}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.tipo && <p className="text-[11px] text-warn mt-1">{errors.tipo.message}</p>}
              </div>
              <div>
                <Label htmlFor="numero" className="mb-1.5 block">Número (opcional)</Label>
                <Input id="numero" placeholder="Ex.: 001/2026" {...register('numero')} />
              </div>
            </div>

            {/* Contratado */}
            <div>
              <Label htmlFor="contratadoNome" className="mb-1.5 block">Contratado (nome)</Label>
              <Input
                id="contratadoNome"
                placeholder="Ex.: Empresa Fornecedora Ltda."
                {...register('contratadoNome', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.contratadoNome && (
                <p className="text-[11px] text-warn mt-1">{errors.contratadoNome.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="contratadoCpfCnpj" className="mb-1.5 block">CPF / CNPJ</Label>
                <Input
                  id="contratadoCpfCnpj"
                  placeholder="000.000.000-00"
                  {...register('contratadoCpfCnpj', {
                    required: 'Obrigatório',
                    setValueAs: (v: string) => fmtCpfCnpj(v),
                    validate: (v) => {
                      const digits = v.replace(/\D/g, '');
                      return digits.length === 11 || digits.length === 14 || 'CPF (11) ou CNPJ (14) dígitos';
                    },
                  })}
                />
                {errors.contratadoCpfCnpj && (
                  <p className="text-[11px] text-warn mt-1">{errors.contratadoCpfCnpj.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="valorTotal" className="mb-1.5 block">Valor total (R$)</Label>
                <Input
                  id="valorTotal"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...register('valorTotal', {
                    required: 'Obrigatório',
                    pattern: { value: /^\d+([.,]\d{1,2})?$/, message: 'Valor inválido' },
                  })}
                />
                {errors.valorTotal && (
                  <p className="text-[11px] text-warn mt-1">{errors.valorTotal.message}</p>
                )}
              </div>
            </div>

            {/* Objeto */}
            <div>
              <Label htmlFor="objeto" className="mb-1.5 block">Objeto</Label>
              <Textarea
                id="objeto"
                placeholder="Descreva o objeto do contrato…"
                rows={3}
                {...register('objeto', {
                  required: 'Obrigatório',
                  minLength: { value: 5, message: 'Mínimo 5 caracteres' },
                })}
              />
              {errors.objeto && <p className="text-[11px] text-warn mt-1">{errors.objeto.message}</p>}
            </div>

            {/* Datas */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dataInicio" className="mb-1.5 block">Data de início</Label>
                <Input
                  id="dataInicio"
                  type="date"
                  {...register('dataInicio', { required: 'Obrigatório' })}
                />
                {errors.dataInicio && (
                  <p className="text-[11px] text-warn mt-1">{errors.dataInicio.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="dataFim" className="mb-1.5 block">Data de fim (opcional)</Label>
                <Input id="dataFim" type="date" {...register('dataFim')} />
              </div>
            </div>
          </SheetBody>

          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting || uploading}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar contrato'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
